import React, { useState, useEffect } from 'react';
import { MdCookie, MdCheck, MdClose, MdSettings } from 'react-icons/md';
import { useTranslation } from '../../hooks/useTranslation';
import * as styles from './cookieBanner.css';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

// Extend Window type for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
  necessary: true, // Always enabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
  // Check if the user has already made a choice
    const cookieConsent = localStorage.getItem('cookie-consent');
    if (!cookieConsent) {
  // Show after a small delay to avoid jarring the UX
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setIsVisible(false);
    
  // Initialize analytics services if accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted'
      });
    }
  };

  const handleAcceptSelected = () => {
    const selectedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(selectedPreferences));
    setIsVisible(false);

  // Configure services according to preferences
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
        ad_storage: preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  const handleRejectAll = () => {
    const rejected = {
  necessary: true, // Necessary always active
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(rejected));
    setIsVisible(false);
  };

  const updatePreference = (type: keyof CookiePreferences, value: boolean) => {
  if (type === 'necessary') return; // Cannot be disabled
    setPreferences(prev => ({ ...prev, [type]: value }));
  };

  if (!isVisible) return null;

  return (
    <div 
      className={styles.cookieBanner}
      role="banner"
      aria-label={t('cookies.banner.title')}
    >
      <div className={styles.cookieContent}>
        <div className={styles.cookieHeader}>
          <MdCookie className={styles.cookieIcon} aria-hidden="true" />
          <h2 className={styles.cookieTitle}>
            {t('cookies.banner.title')}
          </h2>
        </div>

        <div className={styles.cookieBody}>
          <p className={styles.cookieDescription}>
            {t('cookies.banner.description')}
          </p>

          {showDetails && (
            <div className={styles.cookieDetails}>
              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <label className={styles.categoryLabel}>
                    <input
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled
                      aria-describedby="necessary-desc"
                    />
                    <span className={styles.categoryName}>
                      {t('cookies.categories.necessary.title')}
                    </span>
                    <span className={styles.required}>
                      {t('cookies.required')}
                    </span>
                  </label>
                </div>
                <p id="necessary-desc" className={styles.categoryDesc}>
                  {t('cookies.categories.necessary.description')}
                </p>
              </div>

              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <label className={styles.categoryLabel}>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => updatePreference('analytics', e.target.checked)}
                      aria-describedby="analytics-desc"
                    />
                    <span className={styles.categoryName}>
                      {t('cookies.categories.analytics.title')}
                    </span>
                  </label>
                </div>
                <p id="analytics-desc" className={styles.categoryDesc}>
                  {t('cookies.categories.analytics.description')}
                </p>
              </div>

              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <label className={styles.categoryLabel}>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => updatePreference('marketing', e.target.checked)}
                      aria-describedby="marketing-desc"
                    />
                    <span className={styles.categoryName}>
                      {t('cookies.categories.marketing.title')}
                    </span>
                  </label>
                </div>
                <p id="marketing-desc" className={styles.categoryDesc}>
                  {t('cookies.categories.marketing.description')}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className={styles.cookieActions}>
          <button
            onClick={handleRejectAll}
            className={styles.rejectButton}
            aria-label={t('cookies.actions.rejectAll')}
          >
            <MdClose aria-hidden="true" />
            {t('cookies.actions.rejectAll')}
          </button>

          <button
            onClick={() => setShowDetails(!showDetails)}
            className={styles.settingsButton}
            aria-label={t('cookies.actions.customize')}
            aria-expanded={showDetails}
          >
            <MdSettings aria-hidden="true" />
            {t('cookies.actions.customize')}
          </button>

          {showDetails && (
            <button
              onClick={handleAcceptSelected}
              className={styles.acceptSelectedButton}
              aria-label={t('cookies.actions.acceptSelected')}
            >
              <MdCheck aria-hidden="true" />
              {t('cookies.actions.acceptSelected')}
            </button>
          )}

          <button
            onClick={handleAcceptAll}
            className={styles.acceptButton}
            aria-label={t('cookies.actions.acceptAll')}
          >
            <MdCheck aria-hidden="true" />
            {t('cookies.actions.acceptAll')}
          </button>
        </div>

        <div className={styles.cookieFooter}>
          <a 
            href="/legal/privacy" 
            className={styles.privacyLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('cookies.links.privacy')}
          </a>
          <span className={styles.separator}>•</span>
          <a 
            href="/legal/cookies" 
            className={styles.cookiesLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('cookies.links.policy')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
